import type { OrderDetails } from '@/entities/order/model'
import { useCallback } from 'react'
import { useDigitisedPoaEnabled } from '@/entities/state/model'
import { isPoaRequired } from './order.poa'
import { useOrderDetailsNavigation } from './useOrderDetailsNavigation'

/** Search param marking Order Details as showing the Proof of Age checks. */
export const POA_SEARCH_PARAM = 'poa'

/**
 * The Proof of Age completion gate (BD-8245).
 *
 * Every route that can move an order to Completed goes through this hook, so
 * "does this order need checks" has exactly one definition and one place to audit.
 * Spreading the condition across call sites is what makes a compliance gate drift
 * out of sync with itself.
 */
export function usePoaGate() {
  const isPoaEnabled = useDigitisedPoaEnabled()
  const navigateToOrderDetails = useOrderDetailsNavigation()

  /**
   * Whether completing this order must be intercepted to run the checks first.
   *
   * Callers must only consult this for the completion action — gating any other
   * transition would trap legitimate ones such as cancelling.
   */
  const shouldGateCompletion = useCallback(
    (order?: OrderDetails): boolean => isPoaEnabled && isPoaRequired(order),
    [isPoaEnabled],
  )

  /**
   * Open the checks for an order instead of completing it.
   *
   * The flow renders inside Order Details, and which order is showing it is
   * routing state, so it travels as a search param alongside `orderId` — the same
   * way `noBackdrop` already does. Leaving Order Details drops the param, so
   * backing out cannot leave a flow half-open (AC4).
   */
  const openPoaChecks = useCallback(
    (orderId: string) => navigateToOrderDetails(orderId, { [POA_SEARCH_PARAM]: true }),
    [navigateToOrderDetails],
  )

  return { shouldGateCompletion, openPoaChecks }
}
